import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { useGraphQLClient } from "../graphql/client";
import { customerOrdersQuery } from "../graphql/customer-gql";
import { INFINITE_LIST_QUERY_OPTIONS } from "../graphql/queryClient";
import type { ICustomerOrdersQuery } from "../interface/customer";
import { locale } from "../locales";
import { ToastType } from "../types/layout";
import type { IUseOrdersQuery } from "../types/queries";
import { OrderSortKeys } from "../types/storefront.types";

function mergeOrderPages(pages: ICustomerOrdersQuery[] | undefined): ICustomerOrdersQuery | undefined {
  if (!pages?.length) return undefined;

  const firstCustomer = pages[0]?.customer;
  if (!firstCustomer) return pages[0];

  const lastCustomer = pages[pages.length - 1].customer;

  return {
    customer: {
      ...lastCustomer,
      orders: {
        ...lastCustomer.orders,
        edges: pages.flatMap((page) => page.customer?.orders?.edges ?? []),
        pageInfo: lastCustomer.orders.pageInfo,
      },
    },
  } as ICustomerOrdersQuery;
}

export default function useOrdersQuery({ lang, openToast, accessToken, first = 10 }: IUseOrdersQuery) {
  const request = useGraphQLClient();

  const variables = { customerAccessToken: accessToken, first, sortKey: OrderSortKeys.ProcessedAt, reverse: true };

  // accessToken이 있으면 자동 조회. cache-first(staleTime: Infinity)로 목록→상세→뒤로가기 시 페이지/스크롤 복원
  const ordersInfinite = useInfiniteQuery({
    ...INFINITE_LIST_QUERY_OPTIONS,
    queryKey: ["shopifyV2", "orders", { accessToken, first }],
    enabled: !!accessToken,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => request<ICustomerOrdersQuery>(customerOrdersQuery, { ...variables, after: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.customer?.orders?.pageInfo?.hasNextPage ? (lastPage.customer.orders.pageInfo.endCursor ?? undefined) : undefined,
  });

  useEffect(() => {
    if (ordersInfinite.error) openToast({ message: locale(lang).error.server, type: ToastType.Error });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersInfinite.error]);

  const data = useMemo(() => mergeOrderPages(ordersInfinite.data?.pages), [ordersInfinite.data]);

  const hasNextPage = useMemo(() => data?.customer?.orders?.pageInfo.hasNextPage || false, [data?.customer?.orders?.pageInfo]);

  const endCursor = useMemo(() => data?.customer?.orders?.pageInfo.endCursor || "", [data?.customer?.orders?.pageInfo]);

  /** 주문 취소 후 서버 최신 데이터로 목록을 갱신한다 (현재까지 로드된 페이지 전체 재조회) */
  const refetchOrders = () => ordersInfinite.refetch();

  /** 특정 주문 조회 등에서 임의 variables로 직접 조회 (무한스크롤과 별개, 결과 데이터를 반환) */
  const fetchOrders = (fetchVariables: Record<string, unknown>) => request<ICustomerOrdersQuery>(customerOrdersQuery, fetchVariables);

  return {
    data,
    loading: ordersInfinite.isFetching,
    refetch: ordersInfinite.refetch,
    fetchOrders,
    fetchMore: () => ordersInfinite.fetchNextPage(),
    variables,
    orders: data?.customer?.orders?.edges || [],
    hasNextPage,
    endCursor,
    refetchOrders,
  };
}
